import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const catDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 60);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (catDropdownRef.current && !catDropdownRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    if (catOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [catOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link
              to="/"
              className={`font-heading text-lg font-bold transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-950' : 'text-foreground-950'
              }`}
              onClick={() => setMobileOpen(false)}
            >
              CSSBuy Spreadsheet<span className="text-accent-500">.</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-foreground-800 hover:text-foreground-950 hover:bg-black/5'
                }`}
              >
                Home
              </Link>

              {/* Categories Dropdown Desktop */}
              <div className="relative" ref={catDropdownRef}>
                <button
                  onClick={() => setCatOpen(!catOpen)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                    scrolled
                      ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      : 'text-foreground-800 hover:text-foreground-950 hover:bg-black/5'
                  }`}
                >
                  Categorias
                  <i className={catOpen ? 'ri-arrow-up-s-line w-4 h-4 flex items-center justify-center' : 'ri-arrow-down-s-line w-4 h-4 flex items-center justify-center'}></i>
                </button>
                {catOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-background-200/60 shadow-lg overflow-hidden z-50 max-h-[400px] overflow-y-auto">
                    {categories.map((cat, i) => (
                      <a
                        key={i}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                        onClick={() => setCatOpen(false)}
                      >
                        <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                    : 'text-foreground-800 hover:text-foreground-950 hover:bg-black/5'
                }`}
              >
                Guia de Compras
                <i className="ri-external-link-line ml-1 w-3.5 h-3.5 inline-flex items-center justify-center"></i>
              </a>
            </div>

            {/* CTA Button Desktop */}
            <div className="hidden md:block">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap inline-flex items-center justify-center px-5 py-2 bg-foreground-950 text-background-50 text-sm font-semibold rounded-md hover:bg-foreground-800 transition-colors cursor-pointer"
              >
                BUY IT NOW
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer ${
                scrolled ? 'text-foreground-900' : 'text-foreground-950'
              }`}
              aria-label="Menu"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} w-6 h-6 flex items-center justify-center`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bg-white rounded-b-2xl shadow-xl border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
            <div className="p-4 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                Home
              </Link>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center text-foreground-500"></i>
                Guia de Compras
              </a>

              <div className="border-t border-background-100 my-2"></div>
              <p className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categorias</p>
              {categories.map((cat, i) => (
                <a
                  key={i}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                  {cat.name}
                </a>
              ))}

              <div className="pt-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center w-full px-5 py-3 bg-foreground-950 text-background-50 text-sm font-semibold rounded-md hover:bg-foreground-800 transition-colors cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  BUY IT NOW
                  <i className="ri-arrow-right-up-line ml-2 w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}